// Copyright (c) 2021 WiseTime. All rights reserved.

import { Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, mergeMap } from "rxjs/operators"
import * as Actions from "../actions"
import { StatusCode } from "grpc-web"
import { Empty } from "google-protobuf/google/protobuf/empty_pb"
import { grpc } from "../../../app/rpc/executor"
import { preferencesClient } from "../../../app/rpc/client"
import * as Slice from "../slice"
import { DeleteRecentNarrativeRequest } from "../../../generated/wisetime/user/preferences/v4/preferences_pb"
import { isGrpcError } from "../../../lib/rpcutils"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type DeleteRecentNarrativeRpc = (request: DeleteRecentNarrativeRequest) => Observable<Empty>

export const deleteRecentNarrative = (deleteRecentNarrativeRpc: DeleteRecentNarrativeRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.deleteRecentNarrative.match),
      mergeMap(({ payload }) => of(payload)
        .pipe(
          mergeMap(deleteRecentNarrativeRpc),
          mergeMap(_ => of(Slice.deleteCachedNarrative(payload.getRecentNarrative()?.toObject()))),
          catchError(handleError),
        )),
    )

const deleteRecentNarrativeRpc = (request: DeleteRecentNarrativeRequest): Observable<Empty> =>
  grpc(() => preferencesClient.deleteRecentNarrative(request, null))

const handleError = (e: unknown): Observable<Action> => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({ id: "msg_delete_narrative_failed_permission_denied", message: "Failed to delete the recent narrative. You do not have permission to delete the recent narrative." }),
        messageType: "ERROR",
      }))
    }
  }

  return of(showSnackBar({
    message: t({ id: "msg_delete_narrative_failed", message: "Failed to delete the recent narrative. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

export default deleteRecentNarrative(deleteRecentNarrativeRpc)
